export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

interface LoggerConfig {
  level: LogLevel;
  prefix: string;
  enableTimestamp: boolean;
  enableStackTrace: boolean;
}

class Logger {
  private config: LoggerConfig;
  // INFRA-04:允許執行期調整層級;預設 dev=DEBUG、prod=WARN,
  // 舊版固定 WARN 導致開發時所有 info/debug 日誌永久靜音
  private static globalLevel: LogLevel | null = null;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: Logger.getDefaultLevel(),
      prefix: '[StockTrading]',
      enableTimestamp: true,
      enableStackTrace: process.env.NODE_ENV !== 'production',
      ...config,
    };
  }

  private static getDefaultLevel(): LogLevel {
    if (Logger.globalLevel !== null) return Logger.globalLevel;
    return process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.WARN;
  }

  /** 執行期調整全域日誌層級(所有既有/未來實例) */
  static setLevel(level: LogLevel): void {
    Logger.globalLevel = level;
    if (typeof console !== 'undefined') {
      // 同步更新單例,避免與建構時預設不一致
      // eslint-disable-next-line no-console
      console.info(`[StockTrading] log level set to ${LogLevel[level]}`);
    }
    (logger as Logger).config.level = level;
  }

  /** 取得目前層級 */
  getLevel(): LogLevel {
    return this.config.level;
  }

  private formatMessage(level: string, category: string, message: string): string {
    const timestamp = this.config.enableTimestamp ? 
      `[${new Date().toISOString()}]` : '';
    
    return `${timestamp} ${this.config.prefix} [${level}] [${category}] ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.config.level;
  }

  error(category: string, message: string, error?: any) {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const formattedMessage = this.formatMessage('ERROR', category, message);
    // Using console for now, can be replaced with proper logging service in production
    console.error(formattedMessage);
    
    if (error) {
      console.error('Error details:', error);
      if (this.config.enableStackTrace && error.stack) {
        console.error('Stack trace:', error.stack);
      }
    }
  }

  warn(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const formattedMessage = this.formatMessage('WARN', category, message);
    // Using console for now, can be replaced with proper logging service in production
    console.warn(formattedMessage);
    
    if (data) {
      console.warn('Additional data:', data);
    }
  }

  info(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const formattedMessage = this.formatMessage('INFO', category, message);
    // Using console for now, can be replaced with proper logging service in production
    console.info(formattedMessage);
    
    if (data) {
      console.info('Additional data:', data);
    }
  }

  debug(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const formattedMessage = this.formatMessage('DEBUG', category, message);
    console.debug(formattedMessage);
    
    if (data) {
      console.debug('Debug data:', data);
    }
  }

  // Convenience methods for common categories
  service = {
    error: (message: string, error?: any) => this.error('SERVICE', message, error),
    warn: (message: string, data?: any) => this.warn('SERVICE', message, data),
    info: (message: string, data?: any) => this.info('SERVICE', message, data),
    debug: (message: string, data?: any) => this.debug('SERVICE', message, data),
  };

  component = {
    error: (message: string, error?: any) => this.error('COMPONENT', message, error),
    warn: (message: string, data?: any) => this.warn('COMPONENT', message, data),
    info: (message: string, data?: any) => this.info('COMPONENT', message, data),
    debug: (message: string, data?: any) => this.debug('COMPONENT', message, data),
  };

  api = {
    error: (message: string, error?: any) => this.error('API', message, error),
    warn: (message: string, data?: any) => this.warn('API', message, data),
    info: (message: string, data?: any) => this.info('API', message, data),
    debug: (message: string, data?: any) => this.debug('API', message, data),
  };

  auth = {
    error: (message: string, error?: any) => this.error('AUTH', message, error),
    warn: (message: string, data?: any) => this.warn('AUTH', message, data),
    info: (message: string, data?: any) => this.info('AUTH', message, data),
    debug: (message: string, data?: any) => this.debug('AUTH', message, data),
  };

  data = {
    error: (message: string, error?: any) => this.error('DATA', message, error),
    warn: (message: string, data?: any) => this.warn('DATA', message, data),
    info: (message: string, data?: any) => this.info('DATA', message, data),
    debug: (message: string, data?: any) => this.debug('DATA', message, data),
  };

  ui = {
    error: (message: string, error?: any) => this.error('UI', message, error),
    warn: (message: string, data?: any) => this.warn('UI', message, data),
    info: (message: string, data?: any) => this.info('UI', message, data),
    debug: (message: string, data?: any) => this.debug('UI', message, data),
  };

  // Additional categories used in services
  portfolio = {
    error: (message: string, error?: any) => this.error('PORTFOLIO', message, error),
    warn: (message: string, data?: any) => this.warn('PORTFOLIO', message, data),
    info: (message: string, data?: any) => this.info('PORTFOLIO', message, data),
    debug: (message: string, data?: any) => this.debug('PORTFOLIO', message, data),
  };

  transaction = {
    error: (message: string, error?: any) => this.error('TRANSACTION', message, error),
    warn: (message: string, data?: any) => this.warn('TRANSACTION', message, data),
    info: (message: string, data?: any) => this.info('TRANSACTION', message, data),
    debug: (message: string, data?: any) => this.debug('TRANSACTION', message, data),
  };

  watchlist = {
    error: (message: string, error?: any) => this.error('WATCHLIST', message, error),
    warn: (message: string, data?: any) => this.warn('WATCHLIST', message, data),
    info: (message: string, data?: any) => this.info('WATCHLIST', message, data),
    debug: (message: string, data?: any) => this.debug('WATCHLIST', message, data),
  };

  multiAccount = {
    error: (message: string, error?: any) => this.error('MULTI_ACCOUNT', message, error),
    warn: (message: string, data?: any) => this.warn('MULTI_ACCOUNT', message, data),
    info: (message: string, data?: any) => this.info('MULTI_ACCOUNT', message, data),
    debug: (message: string, data?: any) => this.debug('MULTI_ACCOUNT', message, data),
  };

  context = {
    error: (message: string, error?: any) => this.error('CONTEXT', message, error),
    warn: (message: string, data?: any) => this.warn('CONTEXT', message, data),
    info: (message: string, data?: any) => this.info('CONTEXT', message, data),
    debug: (message: string, data?: any) => this.debug('CONTEXT', message, data),
  };

  stockData = {
    error: (message: string, error?: any) => this.error('STOCK_DATA', message, error),
    warn: (message: string, data?: any) => this.warn('STOCK_DATA', message, data),
    info: (message: string, data?: any) => this.info('STOCK_DATA', message, data),
  };

  stockService = {
    error: (message: string, error?: any) => this.error('STOCK_SERVICE', message, error),
    warn: (message: string, data?: any) => this.warn('STOCK_SERVICE', message, data),
    info: (message: string, data?: any) => this.info('STOCK_SERVICE', message, data),
    debug: (message: string, data?: any) => this.debug('STOCK_SERVICE', message, data),
  };

  dividend = {
    error: (message: string, error?: any) => this.error('DIVIDEND', message, error),
    warn: (message: string, data?: any) => this.warn('DIVIDEND', message, data),
    info: (message: string, data?: any) => this.info('DIVIDEND', message, data),
  };

  holdings = {
    error: (message: string, error?: any) => this.error('HOLDINGS', message, error),
    warn: (message: string, data?: any) => this.warn('HOLDINGS', message, data),
    info: (message: string, data?: any) => this.info('HOLDINGS', message, data),
    debug: (message: string, data?: any) => this.debug('HOLDINGS', message, data),
  };

  migration = {
    error: (message: string, error?: any) => this.error('MIGRATION', message, error),
    warn: (message: string, data?: any) => this.warn('MIGRATION', message, data),
    info: (message: string, data?: any) => this.info('MIGRATION', message, data),
    debug: (message: string, data?: any) => this.debug('MIGRATION', message, data),
  };

  stockNameManager = {
    error: (message: string, error?: any) => this.error('STOCK_NAME_MANAGER', message, error),
    warn: (message: string, data?: any) => this.warn('STOCK_NAME_MANAGER', message, data),
    info: (message: string, data?: any) => this.info('STOCK_NAME_MANAGER', message, data),
    debug: (message: string, data?: any) => this.debug('STOCK_NAME_MANAGER', message, data),
  };
}

// Create and export a singleton instance
export const logger = new Logger();

// Export the Logger class for custom instances if needed
export { Logger };
